import { GetInventoryStatusesReq } from '@/services/inventory/inventoryStatus/types'
import { FIELD_NAMES } from '@/pages/inventory/inventoryStatus/constants'
import { IvlInventoryRequest } from '@/services/inventory/inventoryStatus/ivlTypes'
import { IVL_INVENTORY_PAGE_SIZE } from '@/services/inventory/inventoryStatus/ivlConstants'

/** FilterArea 는 필드 종류에 따라 단건(string) 또는 다건(string[]) 을 준다 — 배열로 통일한다. */
const toArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v.map(String).filter(Boolean)
  if (v === undefined || v === null || v === '') return []
  return [String(v)]
}

const toText = (v: unknown): string => (v === undefined || v === null ? '' : String(v))

/**
 * 필터 폼 값을 조회 요청으로 변환한다.
 *
 * 1.0 은 같은 컬럼을 가리키는 필터를 화주별로 다르게 노출했고(BRD_NM vs STC_BRD_NM 등) 2.0 API 는 그
 * 합집합을 그대로 받는다. 신규 디자인은 필터가 고정이므로 **다건을 받는 쪽 파라미터로 통일**한다 —
 * 브랜드명→`stcBrdNm`(mi.brand), 바코드명→`barcode`(oi.BARCODE), 스타일번호→`stcStlNo`(mi.style_number).
 * 상품코드는 1.0 과 같이 단건이며 출력 컬럼 `oi.GDS_CD` 가 아니라 `mi.item_code` 에 걸린다.
 */
export const toInventoryStatusesReq = (values: Record<string, unknown>): GetInventoryStatusesReq => {
  return {
    logisticsServiceTypes: toArray(values[FIELD_NAMES.logisticsServiceTypes]),
    sprNm: toArray(values[FIELD_NAMES.shipperNames]),
    // '전체'는 빈 문자열이고, 빈 값은 toRepeatQuery 가 빼므로 센터 조건 없이 나간다 = 전체 센터 조회(BE 계약 동일).
    whNm: toText(values[FIELD_NAMES.whNm]),
    stcBrdNm: toArray(values[FIELD_NAMES.brandNames]),
    gdsCd: toText(values[FIELD_NAMES.gdsCd]) || undefined,
    barcode: toArray(values[FIELD_NAMES.barcodes]),
    stcStlNo: toArray(values[FIELD_NAMES.styleNumbers]),
  }
}

/** IVL 모드의 센터 선택값은 코드다. whNm을 전송하거나 이름으로 추정하지 않는다. */
export const toIvlInventoryRequest = (values: Record<string, unknown>): IvlInventoryRequest => {
  const { whNm, ...filters } = toInventoryStatusesReq(values)
  return { ...filters, skuId: toArray(values[FIELD_NAMES.skuIds]), warehouseCode: whNm || undefined, includeZeroQuantity: true, page: 0, size: IVL_INVENTORY_PAGE_SIZE }
}

export const hasIvlInventoryFilter = (params: IvlInventoryRequest): boolean => {
  return Object.entries(params).some(([key, value]) =>
    !['page', 'size', 'includeZeroQuantity'].includes(key)
    && (Array.isArray(value) ? value.some((item: string) => item.trim()) : typeof value === 'string' && Boolean(value.trim())))
}
